import { useEffect, useState } from 'react'
import { Download, Share, X } from 'lucide-react'
import { useTranslation } from '@cravo/i18n'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import { partnerAppName } from '@/lib/tenant'

const INSTALL_DISMISSED_KEY = 'cravo-pwa-install-dismissed-at'
const DISMISS_HIDE_DAYS = 14

function isPromptHidden(): boolean {
  if (typeof localStorage === 'undefined') return false
  const value = Number(localStorage.getItem(INSTALL_DISMISSED_KEY) ?? 0)
  if (!value) return false
  return Date.now() - value < DISMISS_HIDE_DAYS * 24 * 60 * 60 * 1000
}

/**
 * Floating install card. Renders when the browser is ready to install or when
 * we're on iOS Safari (which uses a different "Add to Home Screen" flow).
 * Hidden once installed or dismissed for two weeks.
 */
export function InstallAppPrompt() {
  const { t } = useTranslation()
  const { installed, canInstallNatively, supportLevel, install } = useInstallPrompt()
  const [dismissed, setDismissed] = useState<boolean>(() => isPromptHidden())
  const [showIosSheet, setShowIosSheet] = useState(false)
  const [working, setWorking] = useState(false)

  useEffect(() => {
    if (installed) setShowIosSheet(false)
  }, [installed])

  if (installed || dismissed) return null
  if (supportLevel === 'manual-only') return null

  const handleDismiss = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()))
    }
    setDismissed(true)
  }

  const handleInstall = async () => {
    if (canInstallNatively) {
      setWorking(true)
      try {
        const outcome = await install()
        if (outcome === 'dismissed') handleDismiss()
      } finally {
        setWorking(false)
      }
    } else if (supportLevel === 'ios-safari') {
      setShowIosSheet(true)
    }
  }

  return (
    <>
      <div
        className="card"
        style={{
          position: 'fixed',
          left: 20,
          bottom: 20,
          maxWidth: 360,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          background: 'white',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 198,
          border: '1px solid var(--brand-pink-100)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: 'rgba(255,45,151,0.10)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Download size={20} color="var(--brand-pink)" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-950)' }}>
            {t('components.installAppPrompt.title', { appName: partnerAppName() })}
          </div>
          <p style={{ margin: '4px 0 10px', fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5 }}>
            {t('components.installAppPrompt.body', { appName: partnerAppName() })}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn primary sm"
              onClick={() => void handleInstall()}
              disabled={working}
            >
              {working
                ? t('common.working')
                : supportLevel === 'ios-safari'
                  ? t('components.installAppPrompt.howToInstall')
                  : t('components.installAppPrompt.install')}
            </button>
            <button className="btn sm" onClick={handleDismiss} disabled={working}>
              {t('components.installAppPrompt.notNow')}
            </button>
          </div>
        </div>
        <button
          className="btn sm"
          onClick={handleDismiss}
          style={{ padding: '4px 6px' }}
          aria-label={t('components.installAppPrompt.dismiss')}
        >
          <X size={14} />
        </button>
      </div>

      {showIosSheet && (
        <IosInstructionsSheet onClose={() => setShowIosSheet(false)} />
      )}
    </>
  )
}

function IosInstructionsSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(11,11,16,0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1100,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: 420, width: '100%' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="card-title" style={{ justifyContent: 'space-between' }}>
          <span>{t('components.installAppPrompt.iosTitle')}</span>
          <button className="btn sm" onClick={onClose} aria-label={t('components.installAppPrompt.iosClose')}>
            <X size={14} />
          </button>
        </div>
        <ol style={{ margin: '0 0 14px', paddingLeft: 20, fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.7 }}>
          <li>
            {t('components.installAppPrompt.iosStep1Before')}{' '}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Share size={13} /> {t('components.installAppPrompt.iosStep1Share')}</span>{' '}
            {t('components.installAppPrompt.iosStep1After')}
          </li>
          <li>
            {t('components.installAppPrompt.iosStep2Before')} <strong>{t('components.installAppPrompt.iosStep2Action')}</strong>{t('components.installAppPrompt.iosStep2After')}
          </li>
          <li>
            {t('components.installAppPrompt.iosStep3Before')} <strong>{t('components.installAppPrompt.iosStep3Action')}</strong> {t('components.installAppPrompt.iosStep3After')}
          </li>
        </ol>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-500)' }}>
          {t('components.installAppPrompt.iosFooter')}
        </p>
      </div>
    </div>
  )
}
